import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchUpgradeOptions, fetchClientServices, fetchProductGroups, calculateUpgradePrice, calculateRemainingDays, getUpgradeCalculationBreakdown } from "./upgradeAPI";

const initialState = {
  currentService: null,
  upgradeOptions: [],
  clientServices: [],
  productGroups: [],
  selectedService: null,
  selectedGroup: null,
  selectedProduct: null,
  selectedBillingCycle: null,
  upgradePrice: 0,
  upgradeCalculationBreakdown: null,
  remainingDays: 0,
  status: "idle",
  clientServicesStatus: "idle",
  productGroupsStatus: "idle",
  error: "",
  isServiceActive: false,
  unpaidUpgradeInvoices: [],
  servicesWithUnpaidInvoices: [],
};

export const getUpgradeOptions = createAsyncThunk(
  "upgrade/getUpgradeOptions",
  async (params, { rejectWithValue }) => {
    try {
      // Support both old format (single serviceid string) and new format (object with serviceid and targetid)
      const response = await fetchUpgradeOptions(params);
      
      if (response.status === "error") {
        return rejectWithValue(response.message);
      }
      
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getClientServices = createAsyncThunk(
  "upgrade/getClientServices",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchClientServices();
      
      if (response.status === "error") {
        return rejectWithValue(response.message);
      }
      
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getProductGroups = createAsyncThunk(
  "upgrade/getProductGroups",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchProductGroups();
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.error || 'Failed to fetch product groups');
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const upgradeSlice = createSlice({
  name: "upgrade",
  initialState,
  reducers: {
    setSelectedGroup: (state, action) => {
      state.selectedGroup = action.payload;
      state.selectedProduct = null;
      state.selectedBillingCycle = null;
      state.upgradePrice = 0;
      state.upgradeCalculationBreakdown = null;
    },
    setSelectedService: (state, action) => {
      state.selectedService = action.payload;
    },
    setSelectedProduct: (state, action) => {
      // Support both old format (direct product) and new format (object with product and preserveBillingCycle)
      if (action.payload && typeof action.payload === 'object' && action.payload.product !== undefined) {
        // New format: { product: ..., preserveBillingCycle: ... }
        state.selectedProduct = action.payload.product;
        if (!action.payload.preserveBillingCycle) {
          state.selectedBillingCycle = null;
        }
        // If preserveBillingCycle is true, keep the current selectedBillingCycle
      } else {
        // Old format: direct product object (for backward compatibility)
        state.selectedProduct = action.payload;
        state.selectedBillingCycle = null;
      }
      state.upgradePrice = 0;
    },
    setSelectedBillingCycle: (state, action) => {
      state.selectedBillingCycle = action.payload;
      
      // Calculate upgrade price when billing cycle is selected
      if (state.currentService && state.selectedProduct && action.payload) {
        const remainingDays = calculateRemainingDays(state.currentService.nextduedate);
        const upgradePrice = calculateUpgradePrice(
          state.currentService.product_details,
          state.selectedProduct,
          state.currentService.billingcycle,
          action.payload,
          remainingDays
        );
        
        // Get detailed calculation breakdown
        const breakdown = getUpgradeCalculationBreakdown(
          state.currentService.product_details,
          state.selectedProduct,
          state.currentService.billingcycle,
          action.payload,
          remainingDays
        );
        
        state.remainingDays = remainingDays;
        state.upgradePrice = upgradePrice;
        state.upgradeCalculationBreakdown = breakdown;
      }
    },
    resetUpgradeSelection: (state) => {
      state.selectedGroup = null;
      state.selectedProduct = null;
      state.selectedBillingCycle = null;
      state.upgradePrice = 0;
      state.upgradeCalculationBreakdown = null;
      state.remainingDays = 0;
    },
    clearUpgradeData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUpgradeOptions.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(getUpgradeOptions.fulfilled, (state, action) => {
        state.status = "idle";
        state.currentService = action.payload.current_service;
        state.upgradeOptions = action.payload.upgrade_options.products || [];
        
        // Only allow Active services for upgrade
        state.isServiceActive = action.payload.current_service.status === 'Active';
        
        // Calculate remaining days
        if (action.payload.current_service.nextduedate) {
          state.remainingDays = calculateRemainingDays(action.payload.current_service.nextduedate);
        }
        
        // Reset selections
        state.selectedGroup = null;
        state.selectedProduct = null;
        state.selectedBillingCycle = null;
        state.upgradePrice = 0;
        state.upgradeCalculationBreakdown = null;
        
        // Handle unpaid upgrade invoices
        state.unpaidUpgradeInvoices = action.payload.unpaid_upgrade_invoices || [];
      })
      .addCase(getUpgradeOptions.rejected, (state, action) => {
        state.status = "error";
        // Check if the error contains redirect information
        if (action.payload && typeof action.payload === 'object' && action.payload.redirect_to_invoice) {
          state.error = action.payload;
        } else {
          state.error = action.payload || "Failed to fetch upgrade options";
        }
        state.currentService = null;
        state.upgradeOptions = [];
        state.isServiceActive = false;
      })
      .addCase(getClientServices.pending, (state) => {
        state.clientServicesStatus = "loading";
        state.error = "";
      })
      .addCase(getClientServices.fulfilled, (state, action) => {
        state.clientServicesStatus = "idle";
        state.clientServices = action.payload.user_services || [];
        state.selectedService = null;
        // Handle both old and new structure for services with pending upgrades
        state.servicesWithUnpaidInvoices = action.payload.services_with_pending_upgrades || action.payload.services_with_unpaid_invoices || [];
      })
      .addCase(getClientServices.rejected, (state, action) => {
        state.clientServicesStatus = "error";
        state.error = action.payload || "Failed to fetch client services";
        state.clientServices = [];
      })
      .addCase(getProductGroups.pending, (state) => {
        state.productGroupsStatus = "loading";
      })
      .addCase(getProductGroups.fulfilled, (state, action) => {
        state.productGroupsStatus = "idle";
        state.productGroups = action.payload || [];
      })
      .addCase(getProductGroups.rejected, (state, action) => {
        state.productGroupsStatus = "error";
        state.error = action.payload || "Failed to fetch product groups";
        state.productGroups = [];
      });
  },
});

export const {
  setSelectedGroup,
  setSelectedProduct,
  setSelectedBillingCycle,
  setSelectedService,
  resetUpgradeSelection,
  clearUpgradeData,
} = upgradeSlice.actions;

export default upgradeSlice.reducer;
